<template>
  <div v-bind="$attrs"
       v-on="$listeners"
       :disabled="disabled">
    <div :style="{ display: 'flex', alignItems: 'center', height: '100%' }" @click.stop>
      <template v-if="showStartLink">
        <div :class="$style.button"
             :disabled="disabled || !priorPageExists"
             :style="computedButtonStyle"
             @click="selectPage(firstPage)">
          <slot name="start-symbol">
            <div>&#171;</div>
          </slot>
        </div>
      </template>
      <div :class="$style.button"
           :disabled="disabled || !priorPageExists"
           :style="computedButtonStyle"
           @click="selectPage(priorPage)">
        <slot name="ahead-symbol">
          <div>&#8249;</div>
        </slot>
      </div>

      <div v-for="(page, index) in visiblePages" :key="index"
           :style="centralizer">
        <template v-if="page !== null">
          <div :class="$style.number"
               :disabled="disabled || page === null"
               :selected="page === currentPage"
               :style="computedNumberStyle"
               @click.stop="selectPage(page)">
            {{ page }}
          </div>
        </template>
        <template v-else>
          <div :class="$style.ellipsis"
               :style="computedEllipsisStyle"
               @click.stop>
            <slot name="ellipsis-symbol">
              &#183;&#183;&#183;
            </slot>
          </div>
        </template>
      </div>

      <div :class="$style.button"
           :disabled="disabled || !nextPageExists"
           :style="computedButtonStyle"
           @click="selectPage(nextPage)">
        <slot name="next-symbol">
          <div>&#8250;</div>
        </slot>
      </div>
      <template v-if="showEndLink">
        <div :class="$style.button"
             :disabled="disabled || !nextPageExists"
             :style="computedButtonStyle"
             @click="selectPage(lastPage)">
          <slot name="end-symbol">
            <div>&#187;</div>
          </slot>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import { DefaultPseudoClassAttributeNames,
           DefaultPseudoClassVariableNames,
           getPseudoClassesObject,
           filterObjectByArray} from '@js/vue-style-utils'

  export default {
    name: 'BasePagination',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'click',
    },
    props: {
      vmodelValue: { type: Number },
      alwaysShowStartPage: { type: Boolean, required: false, default: false },
      alwaysShowEndPage: { type: Boolean, required: false, default: false },
      buttonStyle: { type: Object, required: false, default: () => {}},
      disabled: { type: Boolean, required: false, default: false },
      ellipsisStyle: { type: Object, required: false, default: () => {}},
      initPage: { type: Number, required: false, default: 1 },
      numberStyle: { type: Object, required: false, default: () => {}},
      pageNum: { type: Number, required: true },
      pageStartFromZero: { type: Boolean, required: false, default: false },
      showEndLink: { type: Boolean, required: false, default: false },
      showStartLink: { type: Boolean, required: false, default: false },
      sidePageSize: { type: Number, required: false, default: 3 },
    },
    data: function () {
      return {
        centralizer: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        },
        computedButtonStyle: {
          height: '32px',
          width: '32px',
        },
        computedEllipsisStyle: {
          height: '32px',
          width: '32px',
        },
        computedNumberStyle: {
          height: '32px',
          width: '32px',
        },
        currentPage: this.pageStartFromZero ? 0 : 1,
        postfixes: ['', 'hover', 'active', 'selected', 'disabled'],
      };
    },
    created: function () {
      this.currentPage = this.vmodelValue;

      let classObject = getPseudoClassesObject(
        this.buttonStyle, DefaultPseudoClassAttributeNames,
        '--base-pagination-button-', DefaultPseudoClassVariableNames,
        this.postfixes);
      let attrs = filterObjectByArray(
        this.buttonStyle, DefaultPseudoClassAttributeNames);

      this.computedButtonStyle = {
        ...this.computedButtonStyle,
        ...this.centralizer,
        ...attrs,
        ...classObject,
      };

      classObject = getPseudoClassesObject(
        this.ellipsisStyle, DefaultPseudoClassAttributeNames,
        '--base-pagination-ellipsis-', DefaultPseudoClassVariableNames,
        this.postfixes);
      attrs = filterObjectByArray(
        this.ellipsisStyle, DefaultPseudoClassAttributeNames);

      this.computedEllipsisStyle = {
        ...this.computedEllipsisStyle,
        ...this.centralizer,
        ...attrs,
        ...classObject,
      };

      classObject = getPseudoClassesObject(
        this.numberStyle, DefaultPseudoClassAttributeNames,
        '--base-pagination-number-', DefaultPseudoClassVariableNames,
        this.postfixes);
      attrs = filterObjectByArray(
        this.numberStyle, DefaultPseudoClassAttributeNames);

      this.computedNumberStyle = {
        ...this.computedNumberStyle,
        ...this.centralizer,
        ...attrs,
        ...classObject,
      };
    },
    computed: {
      firstPage: function () {
        return this.pageStartFromZero ? 0 : 1;
      },
      lastPage: function () {
        return this.pageStartFromZero ? this.pageNum - 1 : this.pageNum;
      },
      priorPageExists: function () {
        return this.currentPage - 1 >= this.firstPage;
      },
      nextPageExists: function () {
        return this.currentPage + 1 <= this.lastPage;
      },
      priorPage: function () {
        return this.priorPageExists ? this.currentPage - 1 : this.currentPage;
      },
      nextPage: function () {
        return this.nextPageExists ? this.currentPage + 1 : this.currentPage;
      },
      pages: function () {
        return Array(this.pageNum).fill(0).map((_, i) => this.pageStartFromZero ? i : i + 1);
      },
      visiblePages: function () {
        let startPage = this.currentPage - this.sidePageSize;
        let endPage = this.currentPage + this.sidePageSize;
        const leftOverFlow = Math.max(this.firstPage - startPage, 0);
        const rightOverFlow = Math.max(endPage - this.lastPage, 0);

        startPage = Math.max(this.firstPage, startPage - rightOverFlow);
        endPage = Math.min(this.lastPage, endPage + leftOverFlow);
        let pages = Array(endPage - startPage + 1).fill(0).map((_, i) => startPage + i);
        if (this.alwaysShowStartPage && pages[0] !== this.firstPage) {
          pages[0] = this.firstPage;
          if (this.sidePageSize - leftOverFlow > 1) {
            pages[1] = null;
          }
        }
        if (this.alwaysShowEndPage && pages[pages.length - 1] !== this.lastPage) {
          pages[pages.length - 1] = this.lastPage;
          if (this.sidePageSize - rightOverFlow > 1) {
            pages[pages.length - 2] = null;
          }
        }
        return pages;
      },
    },
    methods: {
      selectPage: function (page) {
        if (this.disabled) {
          return;
        }

        if (this.currentPage === page) {
          return;
        }

        this.currentPage = page;
        this.$emit('click', this.currentPage);
      },
    },
  };
</script>

<style lang="stylus" module>
  .button
    background-color var(--base-pagination-button-background-color)
    border var(--base-pagination-button-border)
    border-radius var(--base-pagination-button-border-radius)
    border-width var(--base-pagination-button-border-width)
    box-shadow var(--base-pagination-button-box-shadow)
    color var(--base-pagination-button-color)
    cursor var(--base-pagination-button-cursor)
    font-family var(--base-pagination-button-font-family)
    font-size var(--base-pagination-button-font-size)
    font-weight var(--base-pagination-button-font-weight)
    text-decoration var(--base-pagination-button-text-decoration)
    user-select var(--base-pagination-button-user-select)
    &:hover
      background-color var(--base-pagination-button-background-color-hover)
      border var(--base-pagination-button-border-hover)
      border-radius var(--base-pagination-button-border-radius-hover)
      border-width var(--base-pagination-button-border-width-hover)
      box-shadow var(--base-pagination-button-box-shadow-hover)
      color var(--base-pagination-button-color-hover)
      cursor var(--base-pagination-button-cursor-hover)
      font-family var(--base-pagination-button-font-family-hover)
      font-size var(--base-pagination-button-font-size-hover)
      font-weight var(--base-pagination-button-font-weight-hover)
      text-decoration var(--base-pagination-button-text-decoration-hover)
      user-select var(--base-pagination-button-user-select-hover)
    &:active
      background-color var(--base-pagination-button-background-color-active)
      border var(--base-pagination-button-border-active)
      border-radius var(--base-pagination-button-border-radius-active)
      border-width var(--base-pagination-button-border-width-active)
      box-shadow var(--base-pagination-button-box-shadow-active)
      color var(--base-pagination-button-color-active)
      cursor var(--base-pagination-button-cursor-active)
      font-family var(--base-pagination-button-font-family-active)
      font-size var(--base-pagination-button-font-size-active)
      font-weight var(--base-pagination-button-font-weight-active)
      text-decoration var(--base-pagination-button-text-decoration-active)
      user-select var(--base-pagination-button-user-select-active)
    &[disabled]
      background-color var(--base-pagination-button-background-color-disabled)
      border var(--base-pagination-button-border-disabled)
      border-radius var(--base-pagination-button-border-radius-disabled)
      border-width var(--base-pagination-button-border-width-disabled)
      box-shadow var(--base-pagination-button-box-shadow-disabled)
      color var(--base-pagination-button-color-disabled)
      cursor var(--base-pagination-button-cursor-disabled)
      font-family var(--base-pagination-button-font-family-disabled)
      font-size var(--base-pagination-button-font-size-disabled)
      font-weight var(--base-pagination-button-font-weight-disabled)
      text-decoration var(--base-pagination-button-text-decoration-disabled)
      user-select var(--base-pagination-button-user-select-disabled)

  .ellipsis
    background-color var(--base-pagination-ellipsis-background-color)
    border var(--base-pagination-ellipsis-border)
    border-radius var(--base-pagination-ellipsis-border-radius)
    border-width var(--base-pagination-ellipsis-border-width)
    box-shadow var(--base-pagination-ellipsis-box-shadow)
    color var(--base-pagination-ellipsis-color)
    cursor var(--base-pagination-ellipsis-cursor)
    font-family var(--base-pagination-ellipsis-font-family)
    font-size var(--base-pagination-ellipsis-font-size)
    font-weight var(--base-pagination-ellipsis-font-weight)
    text-decoration var(--base-pagination-ellipsis-text-decoration)
    user-select var(--base-pagination-ellipsis-user-select)
    &:hover
      background-color var(--base-pagination-ellipsis-background-color-hover)
      border var(--base-pagination-ellipsis-border-hover)
      border-radius var(--base-pagination-ellipsis-border-radius-hover)
      border-width var(--base-pagination-ellipsis-border-width-hover)
      box-shadow var(--base-pagination-ellipsis-box-shadow-hover)
      color var(--base-pagination-ellipsis-color-hover)
      cursor var(--base-pagination-ellipsis-cursor-hover)
      font-family var(--base-pagination-ellipsis-font-family-hover)
      font-size var(--base-pagination-ellipsis-font-size-hover)
      font-weight var(--base-pagination-ellipsis-font-weight-hover)
      text-decoration var(--base-pagination-ellipsis-text-decoration-hover)
      user-select var(--base-pagination-ellipsis-user-select-hover)
    &:active
      background-color var(--base-pagination-ellipsis-background-color-active)
      border var(--base-pagination-ellipsis-border-active)
      border-radius var(--base-pagination-ellipsis-border-radius-active)
      border-width var(--base-pagination-ellipsis-border-width-active)
      box-shadow var(--base-pagination-ellipsis-box-shadow-active)
      color var(--base-pagination-ellipsis-color-active)
      cursor var(--base-pagination-ellipsis-cursor-active)
      font-family var(--base-pagination-ellipsis-font-family-active)
      font-size var(--base-pagination-ellipsis-font-size-active)
      font-weight var(--base-pagination-ellipsis-font-weight-active)
      text-decoration var(--base-pagination-ellipsis-text-decoration-active)
      user-select var(--base-pagination-ellipsis-user-select-active)
    &[disabled]
      background-color var(--base-pagination-ellipsis-background-color-disabled)
      border var(--base-pagination-ellipsis-border-disabled)
      border-radius var(--base-pagination-ellipsis-border-radius-disabled)
      border-width var(--base-pagination-ellipsis-border-width-disabled)
      box-shadow var(--base-pagination-ellipsis-box-shadow-disabled)
      color var(--base-pagination-ellipsis-color-disabled)
      cursor var(--base-pagination-ellipsis-cursor-disabled)
      font-family var(--base-pagination-ellipsis-font-family-disabled)
      font-size var(--base-pagination-ellipsis-font-size-disabled)
      font-weight var(--base-pagination-ellipsis-font-weight-disabled)
      text-decoration var(--base-pagination-ellipsis-text-decoration-disabled)
      user-select var(--base-pagination-ellipsis-user-select-disabled)

  .number
    background-color var(--base-pagination-number-background-color)
    border var(--base-pagination-number-border)
    border-radius var(--base-pagination-number-border-radius)
    border-width var(--base-pagination-number-border-width)
    box-shadow var(--base-pagination-number-box-shadow)
    color var(--base-pagination-number-color)
    cursor var(--base-pagination-number-cursor)
    font-family var(--base-pagination-number-font-family)
    font-size var(--base-pagination-number-font-size)
    font-weight var(--base-pagination-number-font-weight)
    text-decoration var(--base-pagination-number-text-decoration)
    user-select var(--base-pagination-number-user-select)
    &:hover
      background-color var(--base-pagination-number-background-color-hover)
      border var(--base-pagination-number-border-hover)
      border-radius var(--base-pagination-number-border-radius-hover)
      border-width var(--base-pagination-number-border-width-hover)
      box-shadow var(--base-pagination-number-box-shadow-hover)
      color var(--base-pagination-number-color-hover)
      cursor var(--base-pagination-number-cursor-hover)
      font-family var(--base-pagination-number-font-family-hover)
      font-size var(--base-pagination-number-font-size-hover)
      font-weight var(--base-pagination-number-font-weight-hover)
      text-decoration var(--base-pagination-number-text-decoration-hover)
      user-select var(--base-pagination-number-user-select-hover)
    &:active
      background-color var(--base-pagination-number-background-color-active)
      border var(--base-pagination-number-border-active)
      border-radius var(--base-pagination-number-border-radius-active)
      border-width var(--base-pagination-number-border-width-active)
      box-shadow var(--base-pagination-number-box-shadow-active)
      color var(--base-pagination-number-color-active)
      cursor var(--base-pagination-number-cursor-active)
      font-family var(--base-pagination-number-font-family-active)
      font-size var(--base-pagination-number-font-size-active)
      font-weight var(--base-pagination-number-font-weight-active)
      text-decoration var(--base-pagination-number-text-decoration-active)
      user-select var(--base-pagination-number-user-select-active)
    &[disabled]
      background-color var(--base-pagination-number-background-color-disabled)
      border var(--base-pagination-number-border-disabled)
      border-radius var(--base-pagination-number-border-radius-disabled)
      border-width var(--base-pagination-number-border-width-disabled)
      box-shadow var(--base-pagination-number-box-shadow-disabled)
      color var(--base-pagination-number-color-disabled)
      cursor var(--base-pagination-number-cursor-disabled)
      font-family var(--base-pagination-number-font-family-disabled)
      font-size var(--base-pagination-number-font-size-disabled)
      font-weight var(--base-pagination-number-font-weight-disabled)
      text-decoration var(--base-pagination-number-text-decoration-disabled)
      user-select var(--base-pagination-number-user-select-disabled)
    &[selected]
      background-color var(--base-pagination-number-background-color-selected)
      border var(--base-pagination-number-border-selected)
      border-radius var(--base-pagination-number-border-radius-selected)
      border-width var(--base-pagination-number-border-width-selected)
      box-shadow var(--base-pagination-number-box-shadow-selected)
      color var(--base-pagination-number-color-selected)
      cursor var(--base-pagination-number-cursor-selected)
      font-family var(--base-pagination-number-font-family-selected)
      font-size var(--base-pagination-number-font-size-selected)
      font-weight var(--base-pagination-number-font-weight-selected)
      text-decoration var(--base-pagination-number-text-decoration-selected)
      user-select var(--base-pagination-number-user-select-selected)
</style>
